import React, { useMemo, useState } from "react";
import * as Avatar from "@radix-ui/react-avatar";
import cx from "classnames";
import { Skeleton, type SkeletonProps } from "@picklebet/ui";

import * as styles from "./Silks.module.scss";
import { useCustomerLicence } from "@/features/auth/hooks/useCustomerLicence";
import { CustomerLicenceOptions } from "@/features/auth/models/session";

type SilksProps = {
  silksUrl?: string;
  imageHeight: number;
  competitorNumber?: number | null;
  fallbackClassName?: string;
  skeletonSize?: SkeletonProps["size"];
  className?: string;
};

type RadixImageLoadingStatus = Parameters<
  NonNullable<Avatar.AvatarImageProps["onLoadingStatusChange"]>
>[0];

const Silks = ({
  silksUrl,
  competitorNumber,
  imageHeight,
  fallbackClassName,
  skeletonSize = "sm",
  className = "",
}: SilksProps) => {
  const [imageFetchingStatus, setImageFetchingStatus] =
    useState<RadixImageLoadingStatus>("loading");

  const licence = useCustomerLicence();
  const isNTRWC = licence === CustomerLicenceOptions.NTRWC;

  // custom markets might not have one
  const hasCompetitorNumber =
    typeof competitorNumber === "number" && Number.isFinite(competitorNumber);

  const silkStyle = useMemo(
    () =>
      !hasCompetitorNumber
        ? {}
        : {
            width: `${imageHeight}px`,
            height: `${imageHeight}px`,
            backgroundImage: `url(${silksUrl})`,
            backgroundSize: `cover`,
            backgroundPosition: `${(competitorNumber - 1) * -imageHeight}px 0px`,
            backgroundRepeat: `no-repeat`,
          },
    [silksUrl, competitorNumber, imageHeight],
  );

  if (!hasCompetitorNumber) {
    return (
      <Avatar.Root>
        <Avatar.Fallback className={cx(styles.fallback, fallbackClassName)} />
      </Avatar.Root>
    );
  }

  return (
    <Avatar.Root title={`Competitor number: ${competitorNumber}`}>
      <Avatar.Image
        src={isNTRWC ? silksUrl : undefined}
        asChild
        onLoadingStatusChange={(status) => setImageFetchingStatus(status)}
      >
        {imageFetchingStatus === "loaded" && (
          <div style={silkStyle} className={className} />
        )}
      </Avatar.Image>

      {imageFetchingStatus === "loading" ? (
        <Skeleton
          size={skeletonSize}
          mode="light"
          shape="circle"
          className={cx(styles.skeletonMargin, className)}
        />
      ) : (
        <Avatar.Fallback className={cx(styles.fallback, fallbackClassName)}>
          {competitorNumber}
        </Avatar.Fallback>
      )}
    </Avatar.Root>
  );
};

export { Silks };
